/**
 * "What's next" block (Specs/bid-next-best-action-spec.md §3)
 *
 * Flat action surface at the top of the Bid Overview — 1–3 prioritized,
 * dismissible, deep-linked suggestions from deterministic workflow-stage
 * rules. Zero insights renders nothing: absence is the reward.
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { useNextActionsStore } from '@/stores/next-actions-store'
import { useExtractionStore } from '@/stores/extraction-store'
import { useTenderOverviewStore } from '@/stores/tender-overview-store'
import type { NextActionInsight, NextActionSeverity } from '@/lib/api'
import { cn } from '@/lib/utils'

const SEVERITY_DOT: Record<NextActionSeverity, string> = {
  critical: 'bg-destructive',
  attention: 'bg-[hsl(var(--status-warning))]',
  positive: 'bg-[hsl(var(--status-success))]',
}

/** Rules whose primary action triggers AI work get the sparkle treatment. */
const AI_ACTION_RULES = new Set(['no_response_structure'])

/** Hidden while an analysis is running — the action would start a duplicate run. */
const ANALYSIS_RULE_ID = 'docs_uploaded_not_analyzed'

export function NextActionsBlock({ bidId }: { bidId: string }) {
  const navigate = useNavigate()
  const insights = useNextActionsStore((s) => s.insights)
  const storeBidId = useNextActionsStore((s) => s.bidId)
  const load = useNextActionsStore((s) => s.load)
  const dismiss = useNextActionsStore((s) => s.dismiss)
  const reportShown = useNextActionsStore((s) => s.reportShown)
  const reportClicked = useNextActionsStore((s) => s.reportClicked)
  const isExtracting = useExtractionStore(
    (s) =>
      s.currentBidId === bidId &&
      Object.values(s.categories).some((category) => category.state === 'extracting')
  )
  const isGeneratingOverview = useTenderOverviewStore(
    (s) => s.bidId === bidId && s.status === 'generating'
  )
  const isAnalysisRunning = isExtracting || isGeneratingOverview

  useEffect(() => {
    void load(bidId)
  }, [bidId, load])

  /**
   * Insights are computed server-side from `extraction_runs`, and the store
   * fetches once on mount. That held while workflow stage only changed at human
   * speed — but an analysis finishing in-session changes it at machine speed,
   * leaving the card advising the user to "Run analysis" on the very analysis
   * that just completed (F16). Refetch on the running → idle transition; the
   * run is marked `completed` before the stream's final event, so the refetch
   * observes it.
   */
  const wasAnalysisRunning = useRef(isAnalysisRunning)
  useEffect(() => {
    if (wasAnalysisRunning.current && !isAnalysisRunning) void load(bidId)
    wasAnalysisRunning.current = isAnalysisRunning
  }, [bidId, isAnalysisRunning, load])

  const visible = (storeBidId === bidId ? insights : []).filter(
    (insight) => !(isAnalysisRunning && insight.ruleId === ANALYSIS_RULE_ID)
  )

  useEffect(() => {
    if (visible.length > 0) reportShown(bidId, visible)
  }, [bidId, visible, reportShown])

  if (visible.length === 0) return null

  const handleAction = (insight: NextActionInsight) => {
    reportClicked(bidId, insight)
    void navigate(insight.actionUrl)
  }

  return (
    <div>
      <h3 className="text-sm font-medium">What's next</h3>
      <div className="mt-2 space-y-1">
        {visible.map((insight) => (
          <div
            key={insight.id}
            className="group flex items-center gap-3 rounded-lg px-2 py-1.5 -mx-2 hover:bg-muted/40"
          >
            <span
              className={cn('h-2 w-2 shrink-0 rounded-full', SEVERITY_DOT[insight.severity])}
              aria-label={insight.severity}
            />
            <p className="min-w-0 flex-1 truncate text-sm" title={insight.message}>
              {insight.message}
            </p>
            <Button
              size="sm"
              variant={AI_ACTION_RULES.has(insight.ruleId) ? 'default' : 'outline'}
              className="h-7 shrink-0 gap-1.5 px-2.5 text-xs"
              onClick={() => handleAction(insight)}
            >
              {AI_ACTION_RULES.has(insight.ruleId) && <GradientSparkleIcon className="h-3.5 w-3.5" />}
              {insight.actionLabel}
            </Button>
            <button
              type="button"
              aria-label={`Dismiss: ${insight.message}`}
              onClick={() => void dismiss(bidId, insight)}
              className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
